import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { zynity_api } from '../../config/external-routes';
import './EngagementWidgetPage.css';

const SESSION_TYPE = 'team_setup';
const WIDGET_SOURCE = 'zynity_engagement_widget';
const HOST_SOURCE = 'zynity_engagement_host';
const COLLAPSED_WIDTH = 112;
const COLLAPSED_HEIGHT = 56;
const OPEN_WIDTH = 400;
const OPEN_HEIGHT = 680;

type SessionState = {
  id: number;
  status: string;
  session_type: string;
  current_step_key: string | null;
  current_step: any;
  context_json: Record<string, any>;
  progress: {
    total_steps: number;
    completed_steps: number;
    remaining_steps: number;
    current_step_key: string | null;
  };
};

type ChatAction = {
  type?: string;
  target_key?: string;
  label?: string;
  path?: string;
  url?: string;
  field?: string;
  value?: string;
  fields?: Array<{
    field?: string;
    label?: string;
    value?: string;
  }>;
  confirmation?: {
    domain_key?: string;
    expires_at?: string;
    token?: string;
  };
};

type ChatEntry = {
  id: string;
  role: 'assistant' | 'user' | 'system' | 'error';
  content: string;
  typing?: boolean;
  action?: ChatAction | null;
};

type AiRuntimeState = {
  status: string;
  session_id: number | null;
  runtime_state_id: number | null;
  domain_key: string | null;
  prompt_key: string | null;
  domain?: {
    domain_key?: string;
    label?: string;
    manual_url?: string;
    priority?: number;
  } | null;
  progress?: Record<string, any>;
  next_assistant_message?: string | null;
  should_auto_open?: boolean;
  auto_open_reason?: string | null;
};

type AiConversationMessage = {
  id?: number;
  role?: 'assistant' | 'user' | 'system' | 'error';
  content?: string;
  action?: ChatAction | null;
  created_at?: string | null;
};

type AiChatMessage = {
  role: 'assistant' | 'user';
  content: string;
  action?: ChatAction | null;
};

type WidgetMode = 'ai' | 'session';

const STEP_PROMPTS: Record<string, string> = {
  define_team_name: 'What should we call this team?',
  define_purpose: 'What is the core purpose for this team right now?',
  set_initial_focus: 'What is the first focus area this team should align on?',
  review_and_confirm: 'Review your answers and confirm this setup session.',
};

const STEP_ORDER = [
  'define_team_name',
  'define_purpose',
  'set_initial_focus',
  'review_and_confirm',
];

function titleFromStepKey(stepKey: string): string {
  return stepKey
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function promptFor(stepKey: string, fallback?: string): string {
  return STEP_PROMPTS[stepKey] || fallback || titleFromStepKey(stepKey);
}

function formatResponse(value: any): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => formatResponse(item)).filter(Boolean).join('\n');
  if (typeof value === 'object') {
    return Object.values(value)
      .map((item) => formatResponse(item).trim())
      .filter(Boolean)
      .join('\n');
  }
  return String(value);
}

function getOriginFromUrl(value?: string): string {
  if (!value) return '';
  try {
    return new URL(value, window.location.href).origin;
  } catch (err) {
    return '';
  }
}

function getTrustedHostOrigins(): string[] {
  const origins = new Set<string>();
  const currentOrigin = getOriginFromUrl(window.location.href);
  const referrerOrigin = getOriginFromUrl(document.referrer);

  if (currentOrigin) origins.add(currentOrigin);
  if (referrerOrigin) origins.add(referrerOrigin);

  return Array.from(origins);
}

function getParentTargetOrigin(): string {
  const referrerOrigin = getOriginFromUrl(document.referrer);
  return referrerOrigin || window.location.origin;
}

function isTrustedHostMessage(event: MessageEvent): boolean {
  const data = event?.data;
  if (!data || data.source !== HOST_SOURCE) return false;

  const trustedOrigins = getTrustedHostOrigins();
  return trustedOrigins.length === 0 || trustedOrigins.includes(event.origin);
}

function notifyHost(payload: Record<string, any>) {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ source: WIDGET_SOURCE, ...payload }, getParentTargetOrigin());
  }
}

function isNavigationAction(action?: ChatAction | null): boolean {
  return !!action
    && action.type === 'navigation_target'
    && !!(action.url || action.path)
    && !!action.label;
}

function isPendingConfirmationAction(action?: ChatAction | null): boolean {
  return !!action
    && !!action.confirmation?.token
    && (
      action.type === 'profile_timezone_update_confirmation_needed' ||
      action.type === 'profile_field_update_confirmation_needed' ||
      action.type === 'profile_fields_update_confirmation_needed'
    );
}

function isAffirmativeConfirmation(message: string): boolean {
  const normalized = message
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return [
    'yes',
    'yes please',
    'please do',
    'do it',
    'go ahead',
    'confirm',
    'confirmed',
    'update it',
    'save it',
    'set it',
  ].includes(normalized);
}

function userAutoOpenIdentity(user: any): string {
  const identity =
    user?.id ||
    user?.user?.id ||
    user?.people_id ||
    user?.EmailAddress ||
    user?.email ||
    user?.LoginID;

  return identity ? String(identity) : 'anonymous';
}

function autoOpenStorageKey(user: any, runtime: AiRuntimeState): string {
  const domainKey = runtime?.domain_key || 'unknown';
  return `zynity_zyni_auto_open_seen:${userAutoOpenIdentity(user)}:${domainKey}`;
}

function hasSeenAutoOpen(user: any, runtime: AiRuntimeState): boolean {
  try {
    return window.localStorage?.getItem(autoOpenStorageKey(user, runtime)) === '1';
  } catch (err) {
    return true;
  }
}

function markAutoOpenSeen(user: any, runtime: AiRuntimeState) {
  try {
    window.localStorage?.setItem(autoOpenStorageKey(user, runtime), '1');
  } catch (err) {
    // If storage is unavailable, do not repeatedly force-open the widget.
  }
}

function conversationMessagesFromPayload(payload: any): AiChatMessage[] {
  const messages = payload?.conversation?.messages;
  if (!Array.isArray(messages)) return [];

  return messages
    .filter((message: AiConversationMessage) => (
      (message?.role === 'assistant' || message?.role === 'user') &&
      typeof message?.content === 'string' &&
      message.content.trim() !== ''
    ))
    .map((message: AiConversationMessage) => ({
      role: message.role as 'assistant' | 'user',
      content: String(message.content),
      action: message.action || null,
    }));
}

function latestPendingActionFromHistory(history: AiChatMessage[]): ChatAction | null {
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const action = history[index]?.action || null;
    if (isPendingConfirmationAction(action)) return action;
  }

  return null;
}

export const EngagementWidgetPage: React.FC = () => {
  const user = useSelector((state: any) => state.auth);
  const enabled = !!user?.engagement_engine_enabled;
  const isEmbedded = window.parent && window.parent !== window;

  // Default is minimized; backend can request one-time auto-open for onboarding gaps.
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState<SessionState | null>(null);
  const [runtime, setRuntime] = useState<AiRuntimeState | null>(null);
  const [mode, setMode] = useState<WidgetMode>('session');
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [aiInput, setAiInput] = useState('');
  const [aiHistory, setAiHistory] = useState<AiChatMessage[]>([]);
  const [pendingAction, setPendingAction] = useState<ChatAction | null>(null);
  const [error, setError] = useState('');
  const [assistantTyping, setAssistantTyping] = useState(false);
  const [pendingUserAnswer, setPendingUserAnswer] = useState<string>('');

  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentStep = session?.current_step || null;
  const fields = useMemo(() => {
    const cfg = currentStep?.config_json;
    if (!cfg || !Array.isArray(cfg.fields)) return [];
    return cfg.fields;
  }, [currentStep]);

  const hydrateForm = useCallback((nextSession: SessionState) => {
    const stepKey = nextSession?.current_step_key;
    const stepFields = nextSession?.current_step?.config_json?.fields;
    if (!stepKey || !Array.isArray(stepFields)) {
      setFormValues({});
      return;
    }

    const existing = (nextSession.context_json && nextSession.context_json[stepKey]) || {};
    const nextValues: Record<string, string> = {};
    stepFields.forEach((field: any) => {
      const key = String(field?.key || '');
      if (!key) return;
      nextValues[key] = existing[key] != null ? String(existing[key]) : '';
    });
    setFormValues(nextValues);
  }, []);

  const applySession = useCallback((payload: any) => {
    const nextSession = payload?.session as SessionState;
    if (!nextSession) return;
    setMode('session');
    setRuntime(null);
    setAiHistory([]);
    setPendingAction(null);
    setSession(nextSession);
    hydrateForm(nextSession);
  }, [hydrateForm]);

  const applyRuntime = useCallback((payload: any) => {
    const nextRuntime = payload?.runtime as AiRuntimeState;
    if (!nextRuntime) return;

    setMode('ai');
    setSession(null);
    setFormValues({});
    setRuntime(nextRuntime);

    if (nextRuntime.should_auto_open === true && !hasSeenAutoOpen(user, nextRuntime)) {
      setIsOpen(true);
      markAutoOpenSeen(user, nextRuntime);
    }

    const persistedHistory = conversationMessagesFromPayload(payload);
    if (persistedHistory.length > 0) {
      setAiHistory(persistedHistory);
      setPendingAction(latestPendingActionFromHistory(persistedHistory));
      return;
    }

    setPendingAction(null);

    if (nextRuntime.status === 'complete') {
      setAiHistory([{
        role: 'assistant',
        content: nextRuntime.next_assistant_message || 'You are all caught up for now. If you need help updating anything, I can help.',
      }]);
      return;
    }

    const bootstrapMessage =
      nextRuntime.next_assistant_message ||
      `I can help with ${nextRuntime?.domain?.label || 'this setup'}. What would you like to do first?`;

    setAiHistory([{
      role: 'assistant',
      content: bootstrapMessage,
    }]);
  }, [user]);

  const revealNextPromptWithTyping = useCallback((payload: any, delayMs = 800) => {
    setAssistantTyping(true);
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    typingTimerRef.current = setTimeout(() => {
      applySession(payload);
      setPendingUserAnswer('');
      setAssistantTyping(false);
      typingTimerRef.current = null;
    }, delayMs);
  }, [applySession]);

  const startSession = useCallback(async () => {
    const response = await axios.post(zynity_api.engagement_session_start, {
      session_type: SESSION_TYPE,
    });
    applySession(response.data);
  }, [applySession]);

  const loadLegacySession = useCallback(async () => {
    const response = await axios.get(zynity_api.engagement_session_current, {
      params: { session_type: SESSION_TYPE },
    });
    const currentSession = response?.data?.session;
    if (currentSession?.status === 'paused') {
      await startSession();
      setMessage('Session resumed.');
    } else {
      applySession(response.data);
    }
  }, [applySession, startSession]);

  const shouldFallbackToLegacySession = useCallback((err: any) => {
    const status = err?.response?.status;
    return status === 403 || status === 404 || status === 422;
  }, []);

  const loadSession = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.get(zynity_api.engagement_ai_bootstrap);
      applyRuntime(response.data);
    } catch (err: any) {
      if (shouldFallbackToLegacySession(err)) {
        try {
          await loadLegacySession();
        } catch (legacyErr: any) {
          if (legacyErr?.response?.status === 404) {
            await startSession();
          } else {
            const msg =
              legacyErr?.response?.data?.message ||
              legacyErr?.response?.data?.error ||
              legacyErr?.message ||
              'Unable to load engagement session.';
            setError(msg);
          }
        }
      } else {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Unable to load engagement session.';
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, applyRuntime, loadLegacySession, shouldFallbackToLegacySession, startSession]);

  useEffect(() => {
    const htmlEl = document.documentElement;
    document.body.classList.add('engagement-widget-body');
    htmlEl.classList.add('engagement-widget-html');
    return () => {
      document.body.classList.remove('engagement-widget-body');
      htmlEl.classList.remove('engagement-widget-html');
    };
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    notifyHost({
      type: 'ready',
      enabled,
      view: 'engagement_widget',
    });
  }, [enabled]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isTrustedHostMessage(event)) return;

      const data = event.data;
      if (data.type === 'open') setIsOpen(true);
      if (data.type === 'close') setIsOpen(false);
      if (data.type === 'toggle') setIsOpen((prev) => !prev);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const open = enabled ? isOpen : false;
    const width = !enabled ? 0 : (isOpen ? OPEN_WIDTH : COLLAPSED_WIDTH);
    const height = !enabled ? 0 : (isOpen ? OPEN_HEIGHT : COLLAPSED_HEIGHT);

    notifyHost({
      type: 'state',
      enabled,
      open,
      width,
      height,
    });
  }, [enabled, isOpen]);

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session, message, error, assistantTyping, pendingUserAnswer, isOpen]);

  const chatHistory = useMemo(() => {
    const context = session?.context_json || {};
    const currentStepKey = session?.current_step_key;

    return Object.entries(context)
      .filter(([stepKey, stepResponse]) => {
        if (!stepKey || stepKey === currentStepKey) return false;
        if (!stepResponse || typeof stepResponse !== 'object' || Array.isArray(stepResponse)) return false;
        const formatted = formatResponse(stepResponse);
        return formatted.trim() !== '';
      })
      .map(([stepKey, stepResponse]) => ({
        stepKey,
        prompt: promptFor(stepKey),
        answer: formatResponse(stepResponse),
      }))
      .sort((a, b) => {
        const aIndex = STEP_ORDER.indexOf(a.stepKey);
        const bIndex = STEP_ORDER.indexOf(b.stepKey);
        const aRank = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const bRank = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (aRank !== bRank) return aRank - bRank;
        return a.stepKey.localeCompare(b.stepKey);
      });
  }, [session]);

  const onChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const validateRequired = () => {
    for (const field of fields) {
      if (!field?.required) continue;
      const key = String(field?.key || '');
      const value = (formValues[key] || '').trim();
      if (!value) return `${field?.label || key} is required.`;
    }
    return '';
  };

  const submitStep = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!session?.id || !session?.current_step_key) return;

    const validationError = validateRequired();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setAssistantTyping(true);
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    setError('');
    setMessage('');
    setPendingUserAnswer(formatResponse(formValues));

    try {
      const response = await axios.post(zynity_api.engagement_session_step_submit, {
        session_id: session.id,
        step_key: session.current_step_key,
        response_json: formValues,
      });
      const nextSession = response?.data?.session;
      if (nextSession?.status === 'completed') {
        applySession(response.data);
        setAssistantTyping(false);
        setPendingUserAnswer('');
        setMessage('Great. Your team setup session is completed.');
      } else {
        setFormValues({});
        revealNextPromptWithTyping(response.data);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to submit step.';
      setError(msg);
      setPendingUserAnswer('');
      setAssistantTyping(false);
    } finally {
      setSubmitting(false);
    }
  };

  const submitAiMessage = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedMessage = aiInput.trim();
    if (!trimmedMessage || !runtime?.domain_key || !runtime?.prompt_key) return;

    const nextUserMessage: AiChatMessage = {
      role: 'user',
      content: trimmedMessage,
    };
    const confirmationPendingAction = isAffirmativeConfirmation(trimmedMessage) ? pendingAction : null;
    const historyForRequest = aiHistory.slice(-6).map((item) => ({
      role: item.role,
      content: item.content,
    }));

    setSubmitting(true);
    setAssistantTyping(true);
    setError('');
    setMessage('');
    setAiInput('');
    setAiHistory((prev) => [...prev, nextUserMessage]);

    try {
      const requestPayload: Record<string, any> = {
        domain_key: runtime.domain_key,
        prompt_key: runtime.prompt_key,
        message: trimmedMessage,
        history: historyForRequest,
        context: {
          progress: runtime.progress || {},
        },
      };

      if (confirmationPendingAction) {
        requestPayload.pending_action = confirmationPendingAction;
      }

      const response = await axios.post(zynity_api.engagement_ai_chat, requestPayload);

      const assistantText =
        response?.data?.response?.text ||
        'I received that. Let us continue.';
      const assistantAction = response?.data?.response?.action || null;
      setPendingAction(isPendingConfirmationAction(assistantAction) ? assistantAction : null);

      setAiHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: assistantText,
          action: assistantAction,
        },
      ]);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to send message.';
      setError(msg);
    } finally {
      setAssistantTyping(false);
      setSubmitting(false);
    }
  };

  const isAiMode = mode === 'ai';
  const hasActiveExperience = isAiMode ? !!runtime : !!session;
  const isCompleted = isAiMode ? runtime?.status === 'complete' : session?.status === 'completed';
  const currentPrompt = session?.current_step_key
    ? promptFor(session.current_step_key, currentStep?.title)
    : 'Continue your team setup.';

  const sessionChatEntries = useMemo(() => {
    const entries: ChatEntry[] = [];

    if (session) {
      entries.push({
        id: 'intro',
        role: 'assistant',
        content: "Hi, I'm Zyni. I can guide your team setup step by step.",
      });
    }

    chatHistory.forEach((item, idx) => {
      entries.push({
        id: `h-q-${item.stepKey}-${idx}`,
        role: 'assistant',
        content: item.prompt,
      });
      entries.push({
        id: `h-a-${item.stepKey}-${idx}`,
        role: 'user',
        content: item.answer,
      });
    });

    if (session && !isCompleted) {
      entries.push({
        id: `current-q-${session?.current_step_key || 'unknown'}`,
        role: 'assistant',
        content: currentPrompt,
      });
    }

    if (assistantTyping && pendingUserAnswer) {
      entries.push({
        id: 'pending-user-answer',
        role: 'user',
        content: pendingUserAnswer,
      });
    }

    if (session && !isCompleted && assistantTyping) {
      entries.push({
        id: 'typing-indicator',
        role: 'assistant',
        content: '...',
        typing: true,
      });
    }

    if (session && isCompleted) {
      entries.push({
        id: 'completed-msg',
        role: 'assistant',
        content: 'Session complete. Your setup answers are saved.',
      });
    }

    if (message) entries.push({ id: 'system-msg', role: 'system', content: message });
    if (error) entries.push({ id: 'error-msg', role: 'error', content: error });

    return entries;
  }, [
    session,
    chatHistory,
    assistantTyping,
    pendingUserAnswer,
    isCompleted,
    currentPrompt,
    session?.current_step_key,
    message,
    error,
  ]);

  const aiChatEntries = useMemo(() => {
    const entries: ChatEntry[] = aiHistory.map((item, idx) => ({
      id: `ai-${item.role}-${idx}`,
      role: item.role,
      content: item.content,
      action: item.action || null,
    }));

    if (assistantTyping) {
      entries.push({
        id: 'ai-typing-indicator',
        role: 'assistant',
        content: '...',
        typing: true,
      });
    }

    if (message) entries.push({ id: 'ai-system-msg', role: 'system', content: message });
    if (error) entries.push({ id: 'ai-error-msg', role: 'error', content: error });

    return entries;
  }, [aiHistory, assistantTyping, message, error]);

  const chatEntries = isAiMode ? aiChatEntries : sessionChatEntries;

  if (!enabled) {
    return null;
  }

  if (!isOpen) {
    return (
      <div className={`engagement-widget-root ${isEmbedded ? 'is-embedded' : ''}`}>
        <button
          type="button"
          className="engagement-widget-launcher"
          onClick={() => setIsOpen(true)}
          aria-label="Open Zyni assistant"
        >
          <span className="engagement-widget-launcher-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" focusable="false">
              <path d="M7.5 5.5L8.35 7.65L10.5 8.5L8.35 9.35L7.5 11.5L6.65 9.35L4.5 8.5L6.65 7.65L7.5 5.5Z" fill="currentColor" />
              <path d="M14.5 10L15.55 12.45L18 13.5L15.55 14.55L14.5 17L13.45 14.55L11 13.5L13.45 12.45L14.5 10Z" fill="currentColor" />
            </svg>
          </span>
          <span className="engagement-widget-launcher-label">Zyni</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`engagement-widget-root ${isEmbedded ? 'is-embedded' : ''}`}>
      <aside className="engagement-widget-panel" role="dialog" aria-label="Zyni assistant">
        <header className="engagement-widget-header">
          <h1>Zyni</h1>
          <button
            type="button"
            className="engagement-widget-close"
            onClick={() => setIsOpen(false)}
            aria-label="Minimize assistant"
          >
            x
          </button>
        </header>

        <section className="engagement-widget-feed">
          {loading && <article className="bubble bubble-system">Loading session...</article>}

          {!loading && chatEntries.map((entry) => {
            if (entry.typing) {
              return (
                <article key={entry.id} className="bubble bubble-assistant bubble-typing" aria-live="polite">
                  <span className="typing-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                </article>
              );
            }

            const bubbleContent = (
              <>
                <span>{entry.content}</span>
                {isNavigationAction(entry.action) && (
                  <a
                    className="engagement-widget-link"
                    href={entry.action?.url || entry.action?.path}
                    target="_top"
                    rel="noreferrer"
                  >
                    Open {entry.action?.label}
                  </a>
                )}
              </>
            );

            if (entry.role === 'user') return <article key={entry.id} className="bubble bubble-user">{entry.content}</article>;
            if (entry.role === 'system') return <article key={entry.id} className="bubble bubble-system">{entry.content}</article>;
            if (entry.role === 'error') return <article key={entry.id} className="bubble bubble-error">{entry.content}</article>;
            return <article key={entry.id} className="bubble bubble-assistant">{bubbleContent}</article>;
          })}
          <div ref={chatEndRef} />
        </section>

        {!loading && error && !hasActiveExperience && (
          <div className="engagement-widget-retry">
            <button type="button" className="engagement-widget-send" onClick={loadSession}>
              Try again
            </button>
          </div>
        )}

        {!loading && isAiMode && (
          <form className="engagement-widget-composer" onSubmit={submitAiMessage}>
            <p className="engagement-widget-note">
              {runtime?.domain?.label ? `${runtime.domain.label} flow` : 'Ask Zyni'}
            </p>
            <div className="engagement-widget-fields">
              <textarea
                className="engagement-widget-ai-input"
                placeholder="Ask Zyni or reply here"
                value={aiInput}
                onChange={(event) => setAiInput(event.target.value)}
                disabled={submitting || assistantTyping}
                required
              />
            </div>

            <button className="engagement-widget-send" type="submit" disabled={submitting || assistantTyping || !aiInput.trim()}>
              {submitting ? 'Sending...' : 'Send'}
            </button>
          </form>
        )}

        {!loading && hasActiveExperience && !isCompleted && !isAiMode && (
          <form className="engagement-widget-composer" onSubmit={submitStep}>
            <div className="engagement-widget-fields">
              {fields.length > 0 ? fields.map((field: any) => {
                const key = String(field?.key || '');
                const label = field?.label || key || 'Response';
                const type = field?.type || 'text';
                const required = !!field?.required;
                if (!key) return null;

                if (type === 'textarea') {
                  return (
                    <textarea
                      key={key}
                      id={key}
                      placeholder={label}
                      value={formValues[key] || ''}
                      onChange={onChange(key)}
                      required={required}
                      disabled={submitting || assistantTyping}
                    />
                  );
                }

                return (
                  <input
                    key={key}
                    id={key}
                    type="text"
                    placeholder={label}
                    value={formValues[key] || ''}
                    onChange={onChange(key)}
                    required={required}
                    disabled={submitting || assistantTyping}
                  />
                );
              }) : <p className="engagement-widget-note">Confirm to continue.</p>}
            </div>

            <button className="engagement-widget-send" type="submit" disabled={submitting || assistantTyping}>
              {submitting ? 'Sending...' : fields.length > 0 ? 'Send' : 'Confirm'}
            </button>
          </form>
        )}
      </aside>
    </div>
  );
};
